import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from '../ui/dialog';
import { Search, Plus, Edit, Trash2, Eye, Mic, Users, Settings2 } from 'lucide-react';
import { apiService, SpeechToTextProvider as ApiSpeechToTextProvider, SttModel, LocalizedText, SttProviderType } from '../../services/api';

// Using the API interface instead
type SpeechToTextProvider = ApiSpeechToTextProvider & {
  // Add any additional UI-specific properties if needed
  assignedCustomers?: string[];
};

function getLocalizedText(text: LocalizedText | undefined, lang = 'de'): string {
  if (!text) return '';
  if (typeof text === 'string') return text;
  return text[lang] || text['en'] || Object.values(text)[0] || '';
}

/** Format a stored price for display: empty if undefined, otherwise at least 2 decimals. */
function formatPriceMin2(n?: number): string {
  if (n == null) return '';
  const decimals = String(n).includes('.') ? String(n).split('.')[1].length : 0;
  return n.toFixed(Math.max(2, decimals));
}

export function SpeechToTextPage() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<SpeechToTextProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | { message?: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingProvider, setViewingProvider] = useState<SpeechToTextProvider | null>(null);
  const [editingProvider, setEditingProvider] = useState<SpeechToTextProvider | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    providerType: 'openai' as SttProviderType,
    baseUrl: '',
    models: [] as SttModel[],
    configSchema: '{}',
    status: 'active' as 'active' | 'inactive' | 'deprecated',
    apiKeyRequired: true,
  });
  const [newModel, setNewModel] = useState({ name: '', displayName: '', description: '', pricePerMinuteEur: '' });
  // Index-aligned with formData.models — raw editable price strings so a saved 0.20 stays "0.20".
  const [modelPriceDrafts, setModelPriceDrafts] = useState<string[]>([]);

  // Load providers from API
  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      setLoading(true);
      const data = await apiService.getSpeechToTextProviders();
      setProviders(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const providerTypes = [
    { value: 'deepgram', label: 'Deepgram' },
    { value: 'openai', label: 'OpenAI' },
    { value: 'assemblyai', label: 'AssemblyAI' },
    { value: 'soniox', label: 'Soniox' },
    { value: 'custom', label: 'Custom' }
  ];

  const filteredProviders = providers.filter(provider =>
    provider.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    provider.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleView = (provider: SpeechToTextProvider) => {
    setViewingProvider(provider);
    setIsDialogOpen(true);
  };

  const handleEdit = (provider: SpeechToTextProvider) => {
    setEditingProvider(provider);
    const models = [...provider.models];
    setFormData({
      name: provider.name,
      displayName: provider.displayName,
      providerType: provider.providerType,
      baseUrl: provider.baseUrl || '',
      models,
      configSchema: JSON.stringify(provider.configSchema, null, 2),
      status: provider.status,
      apiKeyRequired: provider.apiKeyRequired,
    });
    setModelPriceDrafts(models.map((m) => formatPriceMin2(m.pricePerMinuteEur)));
    setViewingProvider(null);
    setIsDialogOpen(true);
  };

  const handleAddModel = () => {
    if (newModel.name && newModel.displayName) {
      const trimmedPrice = newModel.pricePerMinuteEur.trim();
      const parsedPrice = trimmedPrice === '' ? undefined : Number(trimmedPrice);
      if (parsedPrice !== undefined && (Number.isNaN(parsedPrice) || parsedPrice < 0)) {
        alert('Preis pro Minute muss eine nicht-negative Zahl sein.');
        return;
      }
      const model: SttModel = {
        name: newModel.name,
        displayName: newModel.displayName,
        ...(newModel.description && { description: newModel.description }),
        ...(parsedPrice !== undefined && { pricePerMinuteEur: parsedPrice }),
      };
      setFormData({
        ...formData,
        models: [...formData.models, model]
      });
      setModelPriceDrafts([...modelPriceDrafts, formatPriceMin2(parsedPrice)]);
      setNewModel({ name: '', displayName: '', description: '', pricePerMinuteEur: '' });
    }
  };

  const handleUpdateModelPrice = (index: number, value: string) => {
    const trimmed = value.trim();
    const parsed = trimmed === '' ? undefined : Number(trimmed);
    if (parsed !== undefined && (Number.isNaN(parsed) || parsed < 0)) return;
    setModelPriceDrafts(modelPriceDrafts.map((d, i) => (i === index ? value : d)));
    setFormData({
      ...formData,
      models: formData.models.map((m, i) => i === index ? { ...m, pricePerMinuteEur: parsed } : m),
    });
  };

  const handleBlurModelPrice = (index: number) => {
    setModelPriceDrafts(modelPriceDrafts.map((d, i) => (i === index ? formatPriceMin2(formData.models[i]?.pricePerMinuteEur) : d)));
  };

  const handleRemoveModel = (index: number) => {
    setFormData({
      ...formData,
      models: formData.models.filter((_, i) => i !== index)
    });
    setModelPriceDrafts(modelPriceDrafts.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      const configObj = JSON.parse(formData.configSchema);
      
      const providerData = {
        name: formData.name,
        displayName: formData.displayName,
        providerType: formData.providerType,
        baseUrl: formData.baseUrl || undefined,
        models: formData.models,
        configSchema: configObj,
        status: formData.status,
        apiKeyRequired: formData.apiKeyRequired,
      };

      if (editingProvider) {
        await apiService.updateSpeechToTextProvider(editingProvider.id, providerData);
      } else {
        await apiService.createSpeechToTextProvider(providerData);
      }

      closeDialog();
      await loadProviders();
    } catch (error: any) {
      alert(error.message || 'Ungültige Konfiguration oder Serverfehler');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Sind Sie sicher, dass Sie diesen Provider löschen möchten?')) {
      try {
        await apiService.deleteSpeechToTextProvider(id);
        await loadProviders();
      } catch (error: any) {
        alert(error.message || 'Fehler beim Löschen des Providers');
      }
    }
  };

  const toggleActive = async (id: string) => {
    const provider = providers.find(p => p.id === id);
    if (!provider) return;

    try {
      const newStatus = provider.status === 'active' ? 'inactive' : 'active';
      await apiService.updateSpeechToTextProvider(id, { status: newStatus });
      await loadProviders();
    } catch (error: any) {
      alert(error.message || 'Fehler beim Aktualisieren des Provider-Status');
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingProvider(null);
    setViewingProvider(null);
    setFormData({
      name: '',
      displayName: '',
      providerType: 'openai',
      baseUrl: '',
      models: [],
      configSchema: '{}',
      status: 'active',
      apiKeyRequired: true,
    });
    setNewModel({ name: '', displayName: '', description: '', pricePerMinuteEur: '' });
    setModelPriceDrafts([]);
  };

  const getTypeBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      'deepgram': 'bg-blue-100 text-blue-800',
      'openai': 'bg-green-100 text-green-800',
      'assemblyai': 'bg-orange-100 text-orange-800',
      'soniox': 'bg-teal-100 text-teal-800',
      'custom': 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'deepgram': 'Deepgram',
      'openai': 'OpenAI',
      'assemblyai': 'AssemblyAI',
      'soniox': 'Soniox',
      'custom': 'Custom'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Speech-to-Text Provider</h1>
            <p className="text-gray-600">Verwaltung der Spracherkennungs-Anbieter</p>
          </div>
        </div>
        <div className="text-center py-12">
          <div className="text-gray-500">Lade Provider...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Speech-to-Text Provider</h1>
            <p className="text-gray-600">Verwaltung der Spracherkennungs-Anbieter</p>
          </div>
        </div>
        <div className="text-center py-12">
          <div className="text-red-500 mb-4">Fehler: {typeof error === 'string' ? error : (error?.message || 'An error occurred')}</div>
          <Button onClick={loadProviders}>Erneut versuchen</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Speech-to-Text Provider</h1>
          <p className="text-gray-600">Verwaltung der Spracherkennungs-Anbieter</p>
        </div>
        <Button onClick={() => { setViewingProvider(null); setEditingProvider(null); setIsDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Neuer Provider
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Provider suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProviders.map((provider) => (
          <Card key={provider.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Mic className="w-5 h-5 text-blue-600" />
                    <CardTitle className="text-lg">{provider.displayName}</CardTitle>
                  </div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Badge className={getTypeBadgeColor(provider.providerType)}>
                      {getTypeLabel(provider.providerType)}
                    </Badge>
                    <Badge variant={provider.status === 'active' ? 'default' : 'secondary'}>
                      {provider.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </div>
                </div>
                <Switch
                  checked={provider.status === 'active'}
                  onCheckedChange={() => toggleActive(provider.id)}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Verfügbare Modelle</h4>
                  <div className="space-y-1">
                    {provider.models.slice(0, 2).map((model, index) => (
                      <div key={index} className="text-sm">
                        <div className="font-medium text-gray-900">{getLocalizedText(model.displayName)}</div>
                        <div className="text-xs text-gray-600">{getLocalizedText(model.description)}</div>
                      </div>
                    ))}
                    {provider.models.length > 2 && (
                      <div className="text-xs text-gray-500">
                        +{provider.models.length - 2} weitere Modelle
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>{provider.assignedCustomers?.length || 0} Kunden</span>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500">
                  Erstellt: {new Date(provider.createdAt).toLocaleDateString('de-DE')}
                </div>
                
                <div className="flex items-center space-x-2 pt-2">
                  <Button variant="ghost" size="sm" onClick={() => handleView(provider)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(provider)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/speech-to-text/${provider.id}/models`)}
                    title="Modelle verwalten"
                  >
                    <Settings2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(provider.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredProviders.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Keine Provider gefunden.</p>
            <Button className="mt-4" onClick={() => { setViewingProvider(null); setEditingProvider(null); setIsDialogOpen(true); }}>
              Ersten Provider erstellen
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewingProvider ? 'Provider Details' :
               editingProvider ? 'Provider bearbeiten' : 'Neuen Provider erstellen'}
            </DialogTitle>
            <DialogDescription>
              {viewingProvider ? 'Detaillierte Ansicht des Speech-to-Text Providers' :
               'Konfigurieren Sie einen Speech-to-Text Provider'}
            </DialogDescription>
          </DialogHeader>

          {viewingProvider ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Provider Information</h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Name:</strong> {viewingProvider.name}</div>
                      <div><strong>Anzeigename:</strong> {viewingProvider.displayName}</div>
                      <div><strong>Typ:</strong> 
                        <Badge className={`ml-2 ${getTypeBadgeColor(viewingProvider.providerType)}`}>
                          {getTypeLabel(viewingProvider.providerType)}
                        </Badge>
                      </div>
                      <div><strong>Status:</strong> {viewingProvider.status === 'active' ? 'Aktiv' : 'Inaktiv'}</div>
                      <div><strong>URL:</strong> <code className="text-xs">{viewingProvider.baseUrl || 'Nicht angegeben'}</code></div>
                      <div><strong>Zugewiesene Kunden:</strong> {viewingProvider.assignedCustomers?.length || 0}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Verfügbare Modelle</h4>
                  <div className="space-y-3">
                    {viewingProvider.models.map((model, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <div className="font-medium text-gray-900">{getLocalizedText(model.displayName)}</div>
                        <div className="text-xs text-gray-500 mt-1">{model.name}</div>
                        <div className="text-sm text-gray-600 mt-1">{getLocalizedText(model.description)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Konfiguration</h4>
                <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <pre className="text-xs text-gray-800">{JSON.stringify(viewingProvider.configSchema, null, 2)}</pre>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={closeDialog}>
                  Schließen
                </Button>
                <Button onClick={() => handleEdit(viewingProvider)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Bearbeiten
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name (technisch)</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="z.B. openai_whisper"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="displayName">Anzeigename</Label>
                  <Input
                    id="displayName"
                    value={formData.displayName}
                    onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                    placeholder="z.B. OpenAI Whisper"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="providerType">Typ</Label>
                  <Select value={formData.providerType} onValueChange={(value: SttProviderType) => setFormData({...formData, providerType: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Typ wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {providerTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="baseUrl">API URL</Label>
                <Input
                  id="baseUrl"
                  value={formData.baseUrl}
                  onChange={(e) => setFormData({...formData, baseUrl: e.target.value})}
                  placeholder="https://api.example.com/v1/speech/recognize"
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Verfügbare Modelle</Label>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddModel}>
                    Modell hinzufügen
                  </Button>
                </div>
                
                <div className="grid grid-cols-4 gap-2 p-3 border border-dashed border-gray-300 rounded-lg">
                  <Input
                    placeholder="Modell Name"
                    value={newModel.name}
                    onChange={(e) => setNewModel({...newModel, name: e.target.value})}
                  />
                  <Input
                    placeholder="Anzeigename"
                    value={newModel.displayName}
                    onChange={(e) => setNewModel({...newModel, displayName: e.target.value})}
                  />
                  <Input
                    placeholder="Beschreibung"
                    value={newModel.description}
                    onChange={(e) => setNewModel({...newModel, description: e.target.value})}
                  />
                  <Input
                    type="number"
                    step="0.000001"
                    min="0"
                    placeholder="€/Min (z.B. 0.0043)"
                    value={newModel.pricePerMinuteEur}
                    onChange={(e) => setNewModel({...newModel, pricePerMinuteEur: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  {formData.models.map((model, index) => (
                    <div key={index} className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">{getLocalizedText(model.displayName)}</div>
                        <div className="text-sm text-gray-600">{model.name}</div>
                        <div className="text-xs text-gray-500">{getLocalizedText(model.description)}</div>
                      </div>
                      <div className="flex flex-col w-40">
                        <Label className="text-xs text-gray-500 mb-1">€/Min</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="z.B. 0.0043"
                          value={modelPriceDrafts[index] ?? ''}
                          onChange={(e) => handleUpdateModelPrice(index, e.target.value)}
                          onBlur={() => handleBlurModelPrice(index)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveModel(index)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="configSchema">Konfiguration Schema (JSON)</Label>
                <Textarea
                  id="configSchema"
                  value={formData.configSchema}
                  onChange={(e) => setFormData({...formData, configSchema: e.target.value})}
                  placeholder='{"language": {"type": "string", "default": "en", "description": "Language code"}}'
                  className="min-h-32 font-mono text-xs"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="status"
                  checked={formData.status === 'active'}
                  onCheckedChange={(checked) => setFormData({...formData, status: checked ? 'active' : 'inactive'})}
                />
                <Label htmlFor="status">Provider aktiv</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="apiKeyRequired"
                  checked={formData.apiKeyRequired}
                  onCheckedChange={(checked) => setFormData({...formData, apiKeyRequired: checked})}
                />
                <Label htmlFor="apiKeyRequired">API Key erforderlich</Label>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={closeDialog}>
                  Abbrechen
                </Button>
                <Button onClick={handleSave}>
                  {editingProvider ? 'Speichern' : 'Erstellen'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}